import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FlaskConical, Database, Trash2 } from 'lucide-react';
import { useEvalDatasets, useDeleteDataset } from '@/hooks/useEvaluator';
import { GeneratePersonasDialog } from './GeneratePersonasDialog';
import { toast } from '@/hooks/use-toast';
import type { LangfuseDataset } from '@/types/evaluator';

interface DatasetBrowserProps {
  orgId: string | undefined;
  selectedDataset: string | null;
  onSelectDataset: (name: string) => void;
  onDatasetDeleted?: () => void;
}

export function DatasetBrowser({ orgId, selectedDataset, onSelectDataset, onDatasetDeleted }: DatasetBrowserProps) {
  const { data, isLoading, error } = useEvalDatasets(orgId);
  const deleteMutation = useDeleteDataset();
  const [generateOpen, setGenerateOpen] = useState(false);

  const datasets = data?.datasets ?? [];

  const sorted = [...datasets].sort((a, b) => {
    const aDate = a.created_at ?? '';
    const bDate = b.created_at ?? '';
    return bDate.localeCompare(aDate);
  });

  return (
    <div className="w-[350px] border-r card-border flex flex-col card-bg shrink-0">
      {/* Header */}
      <div className="px-4 py-3 min-h-[67px] border-b card-border flex items-center gap-3">
        <Database className="h-4 w-4 text-icon" />
        <h3 className="text-base font-medium text-subheading">Personas</h3>
        <span className="ml-auto card-secondary-bg text-subheading text-sm px-2 py-0.5 rounded-md">
          {datasets.length}
        </span>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-auto p-3 space-y-2">
        {isLoading && <p className="text-sm text-muted-foreground px-1">Loading...</p>}
        {error && <p className="text-sm text-destructive px-1">Failed to load personas.</p>}
        {!isLoading && !error && sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Database className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm font-medium text-heading">No personas yet</p>
          </div>
        )}
        {sorted.map((ds: LangfuseDataset) => {
          const isSelected = ds.name === selectedDataset;
          return (
            <div
              key={ds.name}
              className={`group relative w-full rounded-xl border p-3 text-left transition hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm cursor-pointer ${
                isSelected
                  ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                  : 'card-border card-secondary-bg'
              }`}
              onClick={() => onSelectDataset(ds.name)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-0.5 min-w-0 flex-1">
                  <div className="text-sm font-semibold text-heading truncate">{ds.name}</div>
                  {ds.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">{ds.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground">
                    {ds.item_count != null ? `${ds.item_count}` : ''}
                  </span>
                  {orgId && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                          title="Delete population"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete population</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete <span className="font-semibold">{ds.name}</span> and
                            all its items. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMutation.mutate(
                                { name: ds.name, organizationId: orgId },
                                {
                                  onSuccess: () => {
                                    toast({ title: 'Deleted', description: `"${ds.name}" has been deleted.` });
                                    if (ds.name === selectedDataset) {
                                      onDatasetDeleted?.();
                                    }
                                  },
                                  onError: (err) => {
                                    toast({
                                      title: 'Error',
                                      description: err instanceof Error ? err.message : 'Failed to delete.',
                                      variant: 'destructive',
                                    });
                                  },
                                },
                              );
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer with Generate button */}
      {orgId && (
        <div className="border-t card-border px-4 py-3">
          <Button size="sm" className="w-full" onClick={() => setGenerateOpen(true)}>
            <FlaskConical className="mr-2 h-4 w-4" />
            Generate Personas
          </Button>
          <GeneratePersonasDialog
            open={generateOpen}
            onOpenChange={setGenerateOpen}
            organizationId={orgId}
          />
        </div>
      )}
    </div>
  );
}
